<template>
  <div
    class="home-menu h-full flex flex-col items-center justify-center p-6 space-y-8 flex-1 relative"
    :class="{'pb-30': !isMobileDevice, 'px-12': isMobileDevice}"
  >
    <HomeBackdrop />

    <!-- Title Section -->
    <div ref="titleSection" class="text-center space-y-4 relative z-10">
      <h1 class="text-6xl font-bold text-text-primary">
        Let's Play!
      </h1>
      <p class="text-xl text-text-secondary">Pick where to start</p>
    </div>

    <!-- Cards Section - 2 column grid -->
    <div
      class="grid grid-cols-2 gap-16 max-w-4xl w-full relative z-10"
      :class="{'h-60': !isMobileDevice, 'h-40': isMobileDevice}"
    >
      <HomeMenuCard
        ref="myMusicCard"
        :icon="UserIcon"
        title="Single-player"
        subtitle="Your collection"
        @click="$emit('open-music-library')"
      />

      <!-- Quick Start Card - commented to disable for now -->
      <!-- <HomeMenuCard
        ref="quickStartCard"
        :icon="RocketLaunchIcon"
        title="Quick Start"
        subtitle="Upload & play"
        icon-class="text-text-primary"
        @click="$emit('open-quick-start')"
      /> -->

      <HomeMenuCard
        ref="playTogetherCard"
        :icon="UserGroupIcon"
        title="Multi-player"
        subtitle="Create or join a classroom session"
        @click="$emit('open-play-together')"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { useDeviceDetection } from '@/composables/useDeviceDetection'
import { UserGroupIcon, UserIcon } from '@heroicons/vue/24/outline'
import gsap from 'gsap'
import { onMounted, ref } from 'vue'
import HomeBackdrop from './HomeBackdrop.vue'
import HomeMenuCard from './HomeMenuCard.vue'

defineEmits<{
  (e: 'open-music-library'): void
  (e: 'open-play-together'): void
  (e: 'open-quick-start'): void
}>()

const { isMobileDevice } = useDeviceDetection()

const titleSection = ref<HTMLElement>()
const myMusicCard = ref<{ $el: HTMLElement }>()
const quickStartCard = ref<{ $el: HTMLElement }>()
const playTogetherCard = ref<{ $el: HTMLElement }>()

onMounted(() => {
  // Resolve the underlying DOM nodes from the child component refs
  const titleEl = titleSection.value
  const cardEls = [myMusicCard.value?.$el, quickStartCard.value?.$el, playTogetherCard.value?.$el]
    .filter((el): el is HTMLElement => !!el)

  if (!titleEl || cardEls.length === 0) return

  gsap.set([titleEl, ...cardEls], {
    opacity: 0,
    y: 50,
  })

  const tl = gsap.timeline({ delay: 0.2 })

  tl.to(titleEl, {
    opacity: 1,
    y: 0,
    duration: 1,
    ease: 'power3.out',
  })
  .to(cardEls, {
    opacity: 1,
    y: 0,
    duration: 0.8,
    stagger: 0.2,
    ease: 'back.out(1.7)',
    onComplete: () => {
      cardEls.forEach((el) => {
        el.style.scale = ''
        el.style.transitionDuration = '0.3s'
      })
    },
  }, '-=0.5')
})
</script>

<style scoped>
.home-menu {
  overflow: hidden;
}
</style>
